import qs from 'qs';
import { api } from 'src/api/api';
import { StrapiResponse } from 'src/types/http';

export type FidcComparatorFundOption = {
  operationId: number | string;
  name: string;
  cnpj: string;
  label: string;
};

export type FidcComparatorOption = {
  operationId: number | string;
  name: string;
  cnpj: string;
  cota: string | null;
  serie: number | null;
  value: string;
  label: string;
};

export type FidcComparatorPoint = {
  date: string;
  value: number | null;
};

export type FidcComparatorSeries = {
  operationId: number | string;
  cota: string | null;
  serie: number | null;
  name: string | null;
  data: FidcComparatorPoint[];
};

export type FidcComparatorSideBySide = {
  operationId: number | string;
  cota: string | null;
  serie: number | null;
  name: string | null;
  cnpj: string | null;
  classSerie: string;
  serviceProviders: {
    administrator: string | null;
    manager: string | null;
    custodian: string | null;
  };
  profile: {
    collateralAsset: string | null;
    condominium: string | null;
    np: boolean | null;
    mm: boolean | null;
  };
  performance: {
    return1m: number | null;
    return6m: number | null;
    return12m: number | null;
    returnYtd: number | null;
    returnPeriod: number | null;
    cdiReturnPeriod: number | null;
    percentOfCdi: number | null;
    spreadOverCdi: number | null;
    monthsAboveCdi: number;
    monthsBelowCdi: number;
  };
  pl: {
    current: number | null;
    changePeriod: number | null;
    average3m: number | null;
  };
  credit: {
    pdd: number | null;
    pddChangePeriod: number | null;
    atraso?: number | null;
    atrasoOverPl?: number | null;
    atrasoOverPdd?: number | null;
    pddOverAtraso?: number | null;
    pddAverage3m: number | null;
    pddOverPl: number | null;
    dc: number | null;
    creditRightsOverPl: number | null;
  };
  structure: {
    seniorSubordination: number | null;
  };
};

export type FidcComparatorInsight = {
  severity: 'low' | 'medium' | 'high';
  operationId: number | string;
  cota: string | null;
  serie: number | null;
  metric: string;
  title: string;
  description: string;
  currentValue: number | null;
  previousValue: number | null;
};

export type FidcComparatorResponse = {
  period: { from: string; to: string };
  graphPeriod: { from: string; to: string; months: number };
  benchmark: {
    name: string;
    returnPeriod: number | null;
    dataCoverage: { points: number; available: boolean };
  };
  sideBySide: FidcComparatorSideBySide[];
  series: {
    monthlyReturns: FidcComparatorSeries[];
    base100: FidcComparatorSeries[];
    cdiBase100: FidcComparatorPoint[];
    cdiMonthly: FidcComparatorPoint[];
    pl: FidcComparatorSeries[];
    pdd: FidcComparatorSeries[];
    atraso?: FidcComparatorSeries[];
    atrasoOverPl?: FidcComparatorSeries[];
    atrasoOverPdd?: FidcComparatorSeries[];
    pddOverAtraso?: FidcComparatorSeries[];
    pddOverPl: FidcComparatorSeries[];
    creditRightsOverPl?: FidcComparatorSeries[];
    subordination: FidcComparatorSeries[];
  };
  insights: FidcComparatorInsight[];
  dataCoverage: {
    operationId: number | string;
    cota: string | null;
    serie: number | null;
    metrics: { metric: string; points: number; available: boolean }[];
  }[];
};

export type FidcComparatorTableItem = {
  adm?: string | null;
  gest?: string | null;
  cust?: string | null;
  consultores?: string | null;
  ativoLastro?: string | null;
  condominio?: string | null;
  pl?: number | string | null;
  pdd?: number | string | null;
  cvnp?: number | string | null;
  dc?: number | string | null;
  dataRef?: string | null;
  name?: string | null;
  fidc?: string | null;
  cnpj?: string | null;
  operationId: number | string;
  slug?: string | null;
  contrato_lastro?: string | null;
  cedentes?: string | string[] | null;
};

export const getFidcComparatorFundOptions = async (
  search: string,
  page: number,
  pageSize: number,
): Promise<StrapiResponse<FidcComparatorFundOption[]>> => {
  const query = qs.stringify(
    { search, market: 'fidc', pagination: { page, pageSize } },
    { encodeValuesOnly: true },
  );
  const { data } = await api.get(`/operations-detailed/find-by-term?${query}`);
  return data;
};

export type FidcComparatorClassSeriesItem = {
  operationId: string;
  classSeries: string[];
};

export const getFidcComparatorClassSeriesOptions = async (
  operationIds?: Array<number | string>,
  referenceDate?: string,
): Promise<FidcComparatorClassSeriesItem[]> => {
  const query = qs.stringify(
    { operationIds: operationIds?.map(String), referenceDate },
    { arrayFormat: 'comma', encodeValuesOnly: true },
  );

  const { data } = await api.get(
    `/fidc-comparator/class-series-options?${query}`,
  );
  return data;
};

export const getFidcComparatorIntersectedPeriods = async (
  operationIds: Array<number | string>,
): Promise<string[]> => {
  const query = qs.stringify(
    { operationIds: operationIds.map(String) },
    { arrayFormat: 'comma', encodeValuesOnly: true },
  );

  const { data } = await api.get(
    `/fidc-comparator/intersected-periods?${query}`,
  );
  return data;
};

export type HistoricalProfitabilityFundItem = {
  operationId: string;
  name: string;
  classSeries: string;
  rentability: number;
  rentabilityCdi: number;
  cdi: number;
};

export type HistoricalProfitabilityDataPoint = {
  date: string;
  items: HistoricalProfitabilityFundItem[];
};

export type HistoricalProfitabilityPeriodData = {
  data: HistoricalProfitabilityDataPoint[];
  summary: HistoricalProfitabilityFundItem[];
};

export type HistoricalProfitabilityResponse = {
  month: HistoricalProfitabilityPeriodData;
  '3_months': HistoricalProfitabilityPeriodData;
  '6_months': HistoricalProfitabilityPeriodData;
  '12_months': HistoricalProfitabilityPeriodData;
  year: HistoricalProfitabilityPeriodData;
  accumulated: HistoricalProfitabilityPeriodData;
};

export type HistoricalProfitabilityRequestItem = {
  operationId: number | string;
  classSeries: string[];
};

export const getHistoricalProfitability = async (
  referenceDate: string,
  items: HistoricalProfitabilityRequestItem[],
): Promise<HistoricalProfitabilityResponse> => {
  const { data } = await api.post(
    `/fidc-comparator/historical-profitability?referenceDate=${referenceDate}`,
    { items },
  );
  return data;
};

export type FundPerformanceItem = {
  operationId: string;
  name: string;
  periods: { period: string; value: number | null }[];
};

export type FundPerformancePeriodData = {
  items: FundPerformanceItem[];
};

export type FundPerformancePeriodSet = {
  '3_months': FundPerformancePeriodData;
  '6_months': FundPerformancePeriodData;
  '12_months': FundPerformancePeriodData;
  accumulated: FundPerformancePeriodData;
};

export type FundPerformanceResponse = {
  pddOverDelay: FundPerformancePeriodSet;
  dcOverPl: FundPerformancePeriodSet;
};

export const getFundPerformance = async (
  referenceDate: string,
  operationIds: (number | string)[],
): Promise<FundPerformanceResponse> => {
  const query = qs.stringify(
    { referenceDate, operationIds: operationIds.map(String) },
    { arrayFormat: 'comma', encodeValuesOnly: true },
  );
  const { data } = await api.get(`/fidc-comparator/fund-performance?${query}`);
  return data;
};

export const getFidcComparator = async ({
  items,
  baseMonth,
}: {
  items: Array<number | string>;
  baseMonth: string;
  graphPeriod?: string;
}): Promise<FidcComparatorTableItem[]> => {
  const query = qs.stringify(
    {
      operationIds: items.map(String),
      referenceDate: baseMonth,
    },
    { arrayFormat: 'comma', encodeValuesOnly: true },
  );

  const { data } = await api.get(`/fidc-comparator?${query}`);
  return data;
};
